import type {MouseEvent, ReactElement} from 'react';
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import type {Middleware} from '@floating-ui/react';
import {arrow, autoUpdate, computePosition, flip, FloatingArrow, offset, shift, useFloating} from '@floating-ui/react';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import {noop} from '@Utils/general.util';
import {ClickableDiv} from '@Components/clickable-div';
import useWindowSize from '@Hooks/useWindowSize';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {openMoreOptionsModal} from '@Modals/more-options-modal/more-options-modal-library';
import type {MoreOptionsModalProps} from '@Modals/more-options-modal/more-options-modal';
import styles from './dropdown.module.scss';
import type {DropdownPopupProperties} from './dropdown.types';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from './dropdown.types';
import useOutsideClick from '../../hooks/useOutsideClick';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updatePanelPropsForModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';

export interface DropdownProps extends DropdownPopupProperties {
  id?: string;
  open?: boolean;
  className?: string;
  selectorClassNameOnOpen?: string;
  popupClassName?: string;
  popupId?: string;
  /**
   * the selector can be <DropdownDefaultSelectBox /> or <DropdownGhostSelectBox /> or any custom react element.
   * If undefined, the dropdown will render only the popup without a selector.
   */
  selector?: ReactElement<any>;
  /**
   * this will be the container that the dropdown opens. It can be any react element (or more inside a div)
   */
  popup: ReactElement<any>;

  /**
   * Please note, for modals only bottom alignments are supported
   */
  alignment?: ALIGNMENT_TYPE;
  doCloseOnInnerClick?: boolean;
  /**
   * details of flip and shift options are available here: https://floating-ui.com/
   */
  shouldFlip?: boolean;
  shouldShift?: boolean;

  useDefaultArrow?: boolean;
  arrowClasses?: string;
  modalTitle?: string;
  modalContent?: ReactElement<any>;
  modalClassName?: string;
  modalHeight?: string;
  shouldCenterAlignModalTitle?: boolean;
  modalHeaderClasses?: string;
  modalBackButtonClassName?: string;
  showModalForSmallScreen?: boolean;
  modalShowBackButton?: boolean;
  modalShowCloseButton?: boolean;
  closeModalOnBack?: boolean;
  smallScreenWidthThresholdForModal?: number;

  stopClickPropagation?: boolean;
  doesCloseOnOuterClick?: boolean;
  isControlled?: boolean;
  'data-testid'?: string;

  isSmallScreenModalBottomSheet?: boolean;
  isBottomSheetNonBlocking?: boolean;
  takeMaxHeight?: boolean;

  /**
   * please use these functions to determine the open and close states.
   * The default selectbox types have isOpened prop by default but for custom select boxes, opened and closed for select box needs to be
   * handled by the parent component and not the dropdown component
   */
  onClick?(id?: string): void;

  onOpen?(): void;

  onClose?(): void;

  isResumable?: boolean;
  panelIdForModal?: string;
  usePortal?: boolean;
  fixedTopOffset?: number;
  lockPositionOnOpen?: boolean;
}

const DROPDOWN_DEFAULT_MAX_HEIGHT = 360;
const DROPDOWN_OFFSET = 12;

export function Dropdown({
  id = '',
  open = false,
  popupClassName = '',
  popupId = undefined,
  className = '',
  selectorClassNameOnOpen = '',
  onClick = noop,
  onOpen = noop,
  onClose = noop,
  alignment = ALIGNMENT_TYPE.BOTTOM,
  doCloseOnInnerClick = true,
  popupOffset = DROPDOWN_OFFSET,
  popUpHasCustomHeight = false,
  popUpHasCustomWidth = false,
  position = DROPDOWN_POSITION.ABSOLUTE,
  shouldFlip = true,
  shouldShift = true,
  useDefaultArrow = false,
  modalTitle = '',
  modalClassName = '',
  modalHeaderClasses = '',
  modalBackButtonClassName,
  shouldCenterAlignModalTitle = true,
  showModalForSmallScreen = false,
  smallScreenWidthThresholdForModal = MOBILE_MODAL_WIDTH_THRESHOLD,
  modalShowBackButton = false,
  modalShowCloseButton = true,
  closeModalOnBack = true,
  arrowClasses = '',
  leftOffset = 0,
  topOffset = 0,
  stopClickPropagation = false,
  doesCloseOnOuterClick = true,
  isSmallScreenModalBottomSheet = false,
  isBottomSheetNonBlocking = false,
  takeMaxHeight = false,
  isResumable = false,
  setMaxHeightOnVerticalDocumentOverflow = false,
  panelIdForModal,
  isControlled = false,
  usePortal = false,
  fixedTopOffset = 0,
  lockPositionOnOpen = false,
  ...props
}: DropdownProps): ReactElement<any> {
  const [isOpened, setIsOpened] = useState(open);
  const isDropdownOpen = isControlled ? open : isOpened;
  const initialReferenceX = useRef<number | null>(null);
  const initialReferenceY = useRef<number | null>(null);
  const dropdownContainerRef = useRef<HTMLDivElement | null>(null);
  const dropdownPopupMaxHeight = useRef<number | undefined>(undefined);
  const dropdownPopupWidth = useRef<number | undefined>(undefined);
  const {windowWidth} = useWindowSize();
  const arrowRef = useRef(null);
  const hasBeenOpenedOnce = useRef(false);
  const [lockedPosition, setLockedPosition] = useState<{x: number; y: number} | null>(null);
  const lockedPositionRef = useRef<{x: number; y: number} | null>(null);
  lockedPositionRef.current = lockedPosition;
  const dispatch = useAppDispatch();

  const isMoreOptionsModalOpen = useAppSelector((state): boolean => {
    if (!showModalForSmallScreen || !panelIdForModal) {
      return false;
    }
    const modal = state.modals.modalsHashmap[MODALS_IDS.MORE_OPTIONS_MODAL];
    if (!modal?.isOpen) {
      return false;
    }
    return modal.panelProps?.panelId === panelIdForModal;
  });

  const buildMoreOptionsModalPanelProps = (): MoreOptionsModalProps => {
    return {
      className: modalClassName || 'spacing-p-4',
      headerTitle: modalTitle,
      content: props.modalContent ?? props.popup,
      isSmallScreenModalBottomSheet,
      isBottomSheetNonBlocking,
      shouldCenterAlignTitle: shouldCenterAlignModalTitle,
      headerClasses: modalHeaderClasses,
      panelId: panelIdForModal,
      onBack: onClose,
      showBackButton: modalShowBackButton,
      showCloseButton: modalShowCloseButton,
      closeModalOnBack,
      doCloseOnInnerClick,
      modalHeight: props.modalHeight,
      backButtonClassName: modalBackButtonClassName
    };
  };

  useEffect(() => {
    if (!isMoreOptionsModalOpen) {
      return;
    }
    if (!(props.modalContent ?? props.popup)) {
      return;
    }
    dispatch(
      updatePanelPropsForModal({
        modalId: MODALS_IDS.MORE_OPTIONS_MODAL,
        panelProps: buildMoreOptionsModalPanelProps(),
      })
    );
    // props.popup / props.modalContent are intentionally not deps — new ReactElement refs every render would loop the dispatch.
  }, [isMoreOptionsModalOpen]);

  const doesCloseOnOuterClickRef = useRef(doesCloseOnOuterClick);

  useEffect(() => {
    doesCloseOnOuterClickRef.current = doesCloseOnOuterClick;
  }, [doesCloseOnOuterClick]);

  useEffect(() => {
    setInitialPositionForSelector();
    setHeightAndWidthForPopup();
  }, []);

  useEffect(() => {
    if (!isControlled) {
      setIsOpened(open);
    }
  }, [open, isControlled]);

  const setInitialPositionForSelector = (): void => {
    if (refs.reference && refs.reference.current) {
      const boundingBox = refs.reference.current.getBoundingClientRect();
      if (boundingBox) {
        initialReferenceX.current = boundingBox.x;
        initialReferenceY.current = boundingBox.y;
      }
    }
  };

  const getMiddleware = (): Middleware[] => {
    const options = [offset(popupOffset)];
    if (shouldFlip) {
      options.push(flip());
    }
    if (shouldShift) {
      options.push(shift());
    }

    if (fixedTopOffset > 0) {
      options.push({
        name: 'forceFixedTopOffset',
        fn: () => ({y: fixedTopOffset}),
      });
    }

    if (useDefaultArrow) {
      options.push(
        arrow({
          element: arrowRef,
        })
      );
    }

    return options;
  };

  const {x, y, refs, context} = useFloating({
    placement: alignment,
    middleware: getMiddleware(),
    whileElementsMounted: autoUpdate,
  });

  const update = useCallback(async (): Promise<void> => {
    if (lockedPositionRef.current) {
      return;
    }
    if (isDropdownOpen && refs.reference.current && refs.floating.current) {
      const {x, y} = await computePosition(refs.reference.current, refs.floating.current, {
        placement: alignment,
        strategy: 'fixed',
        middleware: getMiddleware(),
      });
      if (refs.reference.current) {
        Object.assign(refs.floating.current.style, {
          left: `${x + leftOffset}px`,
          top: `${fixedTopOffset > 0 ? fixedTopOffset : y + topOffset}px`,
        });
      }
    }
  }, [isDropdownOpen, refs.floating, refs.reference, topOffset, props.arrowOffset]);

  useEffect((): void => {
    if (!isDropdownOpen) {
      setLockedPosition(null);
    }
  }, [isDropdownOpen]);

  useLayoutEffect((): void => {
    if (isDropdownOpen && lockPositionOnOpen && !lockedPosition && x !== null && y !== null) {
      const lockedY = fixedTopOffset > 0 ? fixedTopOffset - topOffset : y;
      setLockedPosition({x, y: lockedY});
    }
  }, [isDropdownOpen, lockPositionOnOpen, lockedPosition, x, y, fixedTopOffset, topOffset]);

  useEffect(() => {
    if (isDropdownOpen) {
      void update();
    }
  }, [topOffset, props.arrowOffset, isDropdownOpen, update]);

  const setMaxHeightIfNeeded = useCallback(async (): Promise<void> => {
    if (isDropdownOpen && refs.reference.current && refs.floating.current) {
      if (refs.floating.current.getBoundingClientRect().y < 0) {
        Object.assign(refs.floating.current.style, {
          maxHeight: `${getHeightForPopup()}px`,
        });
      }
    }
  }, [isDropdownOpen, refs.floating, refs.reference]);

  useLayoutEffect(() => {
    let cleanupFixedDropdownAutoUpdate: VoidFunction | null = null;
    let cleanupMaxHeightAutoUpdate: VoidFunction | null = null;

    if (isDropdownOpen && refs.reference.current && refs.floating.current && position === DROPDOWN_POSITION.FIXED) {
      cleanupFixedDropdownAutoUpdate = autoUpdate(refs.reference.current, refs.floating.current, update);
    }

    if (isDropdownOpen && refs.reference.current && refs.floating.current && setMaxHeightOnVerticalDocumentOverflow) {
      cleanupMaxHeightAutoUpdate = autoUpdate(refs.reference.current, refs.floating.current, setMaxHeightIfNeeded);
    }

    return (): void => {
      if (cleanupFixedDropdownAutoUpdate) {
        cleanupFixedDropdownAutoUpdate();
      }

      if (cleanupMaxHeightAutoUpdate) {
        cleanupMaxHeightAutoUpdate();
      }
    };
  }, [isDropdownOpen, update, refs.floating, refs.reference, setMaxHeightOnVerticalDocumentOverflow]);

  const getPopupClasses = (): string => {
    const classes = [styles.panel, '_default-radius border-s-dark', popupClassName, props.popupSpacingClass ? props.popupSpacingClass : 'spacing-p-2'];
    return classes.join(' ');
  };

  const useOutsideClickCallback = (): void => {
    if (doesCloseOnOuterClick) {
      closeDropdown();
    }
  };

  const closeDropdown = (): void => {
    if (doesCloseOnOuterClick) {
      onClose();
      if (!isControlled) {
        setIsOpened(false);
      }
    }
  };

  const onInnerClick = (): void => {
    if (doCloseOnInnerClick) {
      onClose();
      if (!isControlled) {
        setIsOpened(false);
      }
    }
  };

  const showDropdownSelector = (): ReactElement<any> | null => {
    if (!props.selector) {
      return null;
    }

    if (props.selector.type === DropdownDefaultSelectBox || props.selector.type === DropdownGhostSelectBox) {
      return React.cloneElement(props.selector, {
        isOpened: isDropdownOpen,
        onClick: onClickHandler,
      });
    }

    const existingSelectorClasses = (props.selector.props.className ?? '') as string;
    return React.cloneElement(props.selector, {
      onClick: onClickHandler,
      className: isDropdownOpen ? `${existingSelectorClasses} ${selectorClassNameOnOpen}` : existingSelectorClasses,
    });
  };

  const getDropdownContent = (): ReactElement<any> | null => {
    if (showModalForSmallScreen && windowWidth < smallScreenWidthThresholdForModal) {
      return null;
    }

    if (isDropdownOpen || (isResumable && hasBeenOpenedOnce.current)) {
      const content = (
        <ClickableDiv
          onClick={(e): void => {
            if (doCloseOnInnerClick) {
              e.stopPropagation();
            }
            onInnerClick();
          }}
          id={popupId}
          className={`${getPopupClasses()} ${isDropdownOpen ? '' : ` ${styles.hideFromScreen}`}`}
          style={{
            position: position === DROPDOWN_POSITION.ABSOLUTE ? 'absolute' : 'fixed',
            top: lockedPosition ? `${lockedPosition.y + topOffset}px` : fixedTopOffset > 0 ? fixedTopOffset : y ? `${y + topOffset}px` : `${topOffset}px`,
            left: lockedPosition ? lockedPosition.x + leftOffset : x ? x + leftOffset : '',
            width: dropdownPopupWidth.current,
            maxHeight: props.maxHeight ? props.maxHeight : dropdownPopupMaxHeight.current,
            height: popUpHasCustomHeight && takeMaxHeight ? props.maxHeight : undefined,
          }}
          ref={refs.setFloating}
        >
          {props.popup}
          {useDefaultArrow ? (
            <FloatingArrow
              staticOffset={props.arrowOffset ? Math.abs(fixedTopOffset > 0 ? props.arrowOffset : y ? y + props.arrowOffset : props.arrowOffset) : undefined}
              tipRadius={2}
              className={`${styles.arrow} ${arrowClasses}`}
              ref={arrowRef}
              stroke={PMW_COLORS_NEUTRAL.NEUTRAL_6}
              strokeWidth={0.2}
              context={context}
            />
          ) : null}
        </ClickableDiv>
      );
      return usePortal ? createPortal(content, document.body) : content;
    }
    return null;
  };

  const onClickHandler = (e: MouseEvent): void => {
    if (stopClickPropagation) {
      e.stopPropagation();
    }
    onClick(id);
    if (showModalForSmallScreen && windowWidth < smallScreenWidthThresholdForModal) {
      openMoreOptionsModal(buildMoreOptionsModalPanelProps());
      onOpen();
      return;
    }
    if (isDropdownOpen) {
      onClose();
    } else {
      onOpen();
      if (!hasBeenOpenedOnce.current) {
        hasBeenOpenedOnce.current = true;
      }
    }
    if (!isControlled) {
      setIsOpened(!isOpened);
    }
  };

  const getWidthForThePopup = (): number | undefined => {
    if (!popUpHasCustomWidth && refs.reference && refs.reference.current) {
      const boundingBox = refs.reference.current.getBoundingClientRect();
      return boundingBox.width;
    }

    return undefined;
  };

  const getHeightForPopup = (): number => {
    if (refs.reference && refs.reference.current && refs.floating && refs.floating.current) {
      const boundingBoxReference = refs.reference.current.getBoundingClientRect();
      const boundingBoxPopup = refs.floating.current.getBoundingClientRect();

      // popup is on right
      if (boundingBoxPopup.x > boundingBoxReference.right) {
        return Math.round(window.innerHeight) - 2 * popupOffset;
      }

      // popup is on left
      if (boundingBoxPopup.right < boundingBoxReference.x) {
        return Math.round(window.innerHeight) - 2 * popupOffset;
      }

      // popup is on top
      if (boundingBoxPopup.y < boundingBoxReference.y) {
        return Math.round(boundingBoxReference.y) - 2 * popupOffset;
      }

      // if popup is down
      if (boundingBoxPopup.y > boundingBoxReference.y) {
        return Math.round(window.innerHeight - boundingBoxReference.bottom) - 2 * popupOffset;
      }
    }

    return DROPDOWN_DEFAULT_MAX_HEIGHT;
  };

  const setHeightAndWidthForPopup = (): void => {
    dropdownPopupMaxHeight.current = getHeightForPopup();
    dropdownPopupWidth.current = getWidthForThePopup();
  };

  useOutsideClick(dropdownContainerRef, useOutsideClickCallback, usePortal ? refs.floating.current : null);

  if (dropdownPopupWidth.current === 0 && isDropdownOpen) {
    setHeightAndWidthForPopup();
  }

  return (
    <div
      id={id}
      className={`${styles.container} ${className}`}
      data-testid={props['data-testid']}
      ref={(e): void => {
        refs.setReference(e);
        dropdownContainerRef.current = e;
      }}
    >
      {showDropdownSelector()}
      {getDropdownContent()}
    </div>
  );
}
